import React, { useEffect, useId, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { DialogShell } from "@/shared/ui/DialogShell";
import { useOrgId } from "@/shared/auth/orgContext";
import { stationDirectoryQuery } from "@/features/users/api/usersQueries";
import { getUserName } from "@/features/users/api/userDirectory";
import {
  evidenceSharesQuery,
  useShareEvidence,
  useUnshareEvidence,
  MAX_SHARE_RECIPIENTS,
  MAX_SHARE_MESSAGE_LENGTH,
} from "../api/shareQueries";
import { StationUser, userLabel, initials } from "./shareUsers";

/**
 * Roles whose members can always view any station video (they hold
 * evidence:read_station), independent of the per-user shares. Shown as fixed
 * entries in the "Shared with" list so it's clear they also have access.
 */
const ROLE_ACCESS: { name: string }[] = [
  { name: "Station Admin" },
  { name: "Evidence Viewers" },
];

interface ShareEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  evidenceId: string;
  /** Shown under the title so it's clear which video is being shared. */
  videoTitle?: string;
}

/**
 * Sharing dialog for a single video: pick station members to grant read
 * access, and review/revoke existing shares. Used from both the transcript
 * editor and the generate-report page.
 */
export const ShareEvidenceModal: React.FC<ShareEvidenceModalProps> = ({
  open,
  onClose,
  evidenceId,
  videoTitle,
}) => {
  const titleId = useId();
  const organizationId = useOrgId();
  const { user } = useAuth();

  // Station members for the picker (also validated server-side).
  const directory = useQuery({
    ...stationDirectoryQuery(organizationId ?? ""),
    enabled: open && !!organizationId,
  });

  const sharesQuery = useQuery({
    ...evidenceSharesQuery(evidenceId),
    enabled: open && !!evidenceId,
  });
  const shares = useMemo(
    () =>
      [...(sharesQuery.data?.shares ?? [])].sort(
        (a, b) => b.createdAt - a.createdAt,
      ),
    [sharesQuery.data],
  );

  const share = useShareEvidence(evidenceId);
  const unshare = useUnshareEvidence(evidenceId);

  const [selected, setSelected] = useState<StationUser[]>([]);
  const [message, setMessage] = useState("");

  // Fresh picker and message every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected([]);
      setMessage("");
    }
  }, [open]);

  // Directory-first name lookup, falling back to any other cached user list
  // (getUserName) and finally the raw id — same approach as uploader names.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of (directory.data?.users ?? []) as StationUser[]) {
      if (u.id) map.set(u.id, userLabel(u));
    }
    return map;
  }, [directory.data]);
  const resolveName = (userId: string) =>
    nameById.get(userId) ?? getUserName(userId) ?? userId;

  // Candidates: station members who aren't the caller and aren't already
  // shared. Recipients get read access, so sharing with yourself is moot.
  const options = useMemo(() => {
    const sharedIds = new Set(shares.map((s) => s.sharedWithUserId));
    return ((directory.data?.users ?? []) as StationUser[])
      .filter((u) => u.id && u.id !== user?.id && !sharedIds.has(u.id))
      .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
  }, [directory.data, shares, user?.id]);

  const handleShare = () => {
    if (selected.length === 0) return;
    share.mutate(
      { userIds: selected.map((u) => u.id), message },
      {
        onSuccess: () => {
          setSelected([]);
          setMessage("");
        },
      },
    );
  };

  const busy = share.isPending || unshare.isPending;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      closeDisabled={busy}
      maxWidth={480}
      titleId={titleId}
    >
      <DialogTitle id={titleId} sx={{ pr: 6 }}>
        Share video
        {videoTitle && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {videoTitle}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Grant view access to other members of your station.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={options}
            value={selected}
            onChange={(_e, value) => setSelected(value)}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionDisabled={() => selected.length >= MAX_SHARE_RECIPIENTS}
            loading={directory.isLoading}
            disabled={share.isPending}
            noOptionsText={
              directory.isError
                ? "Couldn't load station members"
                : "No one left to share with"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selected.length === 0 ? "Add people…" : undefined}
              />
            )}
          />
          <Button
            variant="contained"
            onClick={handleShare}
            loading={share.isPending}
            disabled={selected.length === 0}
            startIcon={<PersonAddAlt1Icon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            Share
          </Button>
        </Box>

        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          label="Message (optional)"
          placeholder="Add a note for the people you're sharing with…"
          value={message}
          onChange={(e) =>
            setMessage(e.target.value.slice(0, MAX_SHARE_MESSAGE_LENGTH))
          }
          disabled={share.isPending}
          inputProps={{ maxLength: MAX_SHARE_MESSAGE_LENGTH }}
          helperText={`${message.length}/${MAX_SHARE_MESSAGE_LENGTH}`}
          FormHelperTextProps={{ sx: { textAlign: "right", m: 0, mt: 0.5 } }}
          sx={{ mt: 1.5 }}
        />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Shared with{sharesQuery.data ? ` (${shares.length})` : ""}
        </Typography>

        {/* Role-based access: these roles can always view the video, so they
            appear as fixed entries alongside the explicitly-shared people. */}
        <List dense disablePadding>
          {ROLE_ACCESS.map((r) => (
            <ListItem key={r.name} disableGutters>
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Avatar sx={{ width: 32, height: 32, fontSize: "0.85rem" }}>
                  {initials(r.name)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={r.name}
                secondary="Always has access"
                primaryTypographyProps={{ variant: "body2" }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItem>
          ))}
        </List>

        {sharesQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : sharesQuery.isError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => sharesQuery.refetch()}
              >
                Retry
              </Button>
            }
          >
            Couldn't load who this video is shared with.
          </Alert>
        ) : shares.length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", py: 1 }}>
            This video hasn't been shared with anyone yet.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 280, overflowY: "auto" }}>
            {shares.map((s) => {
              const name = resolveName(s.sharedWithUserId);
              const removing =
                unshare.isPending &&
                unshare.variables?.includes(s.sharedWithUserId);
              return (
                <ListItem
                  key={s.sharedWithUserId}
                  disableGutters
                  secondaryAction={
                    removing ? (
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                    ) : (
                      <Tooltip title="Remove access">
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={`Remove access for ${name}`}
                          onClick={() => unshare.mutate([s.sharedWithUserId])}
                          disabled={unshare.isPending}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 44 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: "0.85rem" }}>
                      {initials(name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={`Shared by ${resolveName(s.createdBy)} · ${new Date(
                      s.createdAt,
                    ).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}`}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
    </DialogShell>
  );
};
